(() => {
  const elementName = 'tutorial-video-library';

  if (customElements.get(elementName)) return;

  class TutorialVideoLibrary extends HTMLElement {
    static extractYouTubeId(value) {
      if (typeof value !== 'string' || value.trim() === '') return null;

      let url;
      try {
        url = new URL(value.trim());
      } catch (_error) {
        return null;
      }

      const host = url.hostname.toLowerCase();
      const allowedYouTubeHosts = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
      let videoId = null;

      if (host === 'youtu.be') {
        videoId = url.pathname.split('/').filter(Boolean)[0] || null;
      } else if (allowedYouTubeHosts.has(host)) {
        if (url.pathname === '/watch') {
          videoId = url.searchParams.get('v');
        } else {
          const pathParts = url.pathname.split('/').filter(Boolean);
          if (pathParts[0] === 'shorts' || pathParts[0] === 'embed') {
            videoId = pathParts[1] || null;
          }
        }
      }

      return /^[A-Za-z0-9_-]{11}$/.test(videoId || '') ? videoId : null;
    }

    static resolveSource(dataset = {}) {
      const shopifyUrl = typeof dataset.shopifySrc === 'string' ? dataset.shopifySrc.trim() : '';
      if (shopifyUrl) {
        return {
          kind: 'shopify',
          url: shopifyUrl,
          mimeType: (dataset.shopifyMime || '').trim() || 'video/mp4',
        };
      }

      const youtubeId = TutorialVideoLibrary.extractYouTubeId(dataset.youtubeUrl || '');
      return youtubeId ? { kind: 'youtube', id: youtubeId } : null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };

      this.categoryTriggers = Array.from(this.querySelectorAll('[data-category-trigger]'));
      this.categoryPanels = Array.from(this.querySelectorAll('[data-category-panel]'));
      this.mobileCategoryLabel = this.querySelector('[data-mobile-category-label]');
      this.drawerOpenButton = this.querySelector('[data-drawer-open]');
      this.drawerCloseButton = this.querySelector('[data-drawer-close]');
      this.drawerOverlay = this.querySelector('[data-drawer-overlay]');
      this.dialog = this.querySelector('[data-video-dialog]');
      this.dialogTitle = this.querySelector('[data-video-dialog-title]');
      this.videoStage = this.querySelector('[data-video-stage]');
      this.videoCloseButton = this.querySelector('[data-video-close]');

      this.classList.add('is-enhanced');

      this.categoryTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.selectCategory(trigger.dataset.categoryId), listenerOptions);
        trigger.addEventListener('keydown', (event) => this.handleCategoryKeydown(event), listenerOptions);
      });

      this.querySelectorAll('[data-video-trigger]').forEach((trigger) => {
        if (!TutorialVideoLibrary.resolveSource(trigger.dataset)) {
          trigger.hidden = true;
          return;
        }
        trigger.addEventListener('click', () => this.openVideo(trigger), listenerOptions);
      });

      this.refreshEmptyStates();

      this.drawerOpenButton?.addEventListener('click', () => this.openDrawer(), listenerOptions);
      this.drawerCloseButton?.addEventListener('click', () => this.closeDrawer(true), listenerOptions);
      this.drawerOverlay?.addEventListener('click', () => this.closeDrawer(true), listenerOptions);
      this.videoCloseButton?.addEventListener('click', () => this.closeVideo(), listenerOptions);

      this.dialog?.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.closeVideo();
        },
        listenerOptions,
      );
      this.dialog?.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.closeVideo();
        },
        listenerOptions,
      );
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && this.classList.contains('is-drawer-open')) {
            this.closeDrawer(true);
          }
        },
        listenerOptions,
      );

      this.setYouTubeThumbnails();
      if (this.categoryTriggers[0]) {
        this.selectCategory(this.categoryTriggers[0].dataset.categoryId);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.closeVideo(false);
      this.classList.remove('is-drawer-open');
      this.syncScrollLock();
      this.initialized = false;
    }

    selectCategory(categoryId, focusTrigger = false) {
      const activeTrigger = this.categoryTriggers?.find((trigger) => trigger.dataset.categoryId === categoryId);
      if (!activeTrigger) return;
      const drawerWasOpen = this.classList?.contains('is-drawer-open') || false;

      this.categoryTriggers.forEach((trigger) => {
        const isActive = trigger === activeTrigger;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', String(isActive));
        trigger.tabIndex = isActive ? 0 : -1;
      });

      this.categoryPanels?.forEach((panel) => {
        const isActive = panel.dataset.categoryId === categoryId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      const label = activeTrigger.querySelector('.tvl__navigation-label')?.textContent?.trim();
      if (this.mobileCategoryLabel && label) this.mobileCategoryLabel.textContent = label;
      if (focusTrigger) activeTrigger.focus();
      this.closeDrawer(drawerWasOpen);
    }

    refreshEmptyStates() {
      this.categoryPanels?.forEach((panel) => {
        const cards = Array.from(panel.querySelectorAll('[data-video-trigger]'));
        const hasVisibleCard = cards.some((card) => !card.hidden);
        let emptyState = panel.querySelector('[data-empty-state]');

        if (!hasVisibleCard && !emptyState && this.dataset.emptyMessage) {
          emptyState = document.createElement('p');
          emptyState.className = 'tvl__empty';
          emptyState.dataset.emptyState = '';
          emptyState.textContent = this.dataset.emptyMessage;
          panel.append(emptyState);
        }

        if (emptyState) emptyState.hidden = hasVisibleCard;
      });
    }

    handleCategoryKeydown(event) {
      const currentIndex = this.categoryTriggers.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.categoryTriggers.length;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.categoryTriggers.length) % this.categoryTriggers.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.categoryTriggers.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.selectCategory(this.categoryTriggers[nextIndex].dataset.categoryId, true);
    }

    openDrawer() {
      this.classList.add('is-drawer-open');
      this.drawerOpenButton?.setAttribute('aria-expanded', 'true');
      this.syncScrollLock();
      this.drawerCloseButton?.focus();
    }

    closeDrawer(restoreFocus = false) {
      const wasOpen = this.classList.contains('is-drawer-open');
      this.classList.remove('is-drawer-open');
      this.drawerOpenButton?.setAttribute('aria-expanded', 'false');
      this.syncScrollLock();
      if (restoreFocus && wasOpen) this.drawerOpenButton?.focus();
    }

    syncScrollLock() {
      const shouldLock = this.classList.contains('is-drawer-open') || Boolean(this.dialog?.open);
      document.body?.classList.toggle('tvl-scroll-locked', shouldLock);
    }

    setYouTubeThumbnails() {
      this.querySelectorAll('[data-youtube-thumbnail]').forEach((thumbnail) => {
        const trigger = thumbnail.closest('[data-video-trigger]');
        const source = TutorialVideoLibrary.resolveSource(trigger?.dataset);
        if (source?.kind === 'youtube') {
          thumbnail.style.backgroundImage = `url(https://i.ytimg.com/vi/${source.id}/hqdefault.jpg)`;
        }
      });
    }

    openVideo(trigger) {
      const source = TutorialVideoLibrary.resolveSource(trigger?.dataset);
      if (!source || !this.dialog || !this.videoStage) return;

      this.videoStage.replaceChildren();
      this.activeTrigger = trigger;
      if (this.dialogTitle) this.dialogTitle.textContent = trigger.dataset.videoTitle || 'Video';

      let player;
      if (source.kind === 'shopify') {
        player = document.createElement('video');
        player.controls = true;
        player.autoplay = true;
        player.playsInline = true;
        player.preload = 'metadata';

        const videoSource = document.createElement('source');
        videoSource.src = source.url;
        videoSource.type = source.mimeType;
        player.append(videoSource);
      } else {
        player = document.createElement('iframe');
        player.src = `https://www.youtube-nocookie.com/embed/${source.id}?autoplay=1&rel=0`;
        player.title = trigger.dataset.videoTitle || 'Video';
        player.loading = 'eager';
        player.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        player.allowFullscreen = true;
        player.referrerPolicy = 'strict-origin-when-cross-origin';
      }

      this.videoStage.append(player);
      if (!this.dialog.open) this.dialog.showModal();
      this.syncScrollLock();

      if (source.kind === 'shopify') {
        const playPromise = player.play();
        playPromise?.catch(() => {});
      }
    }

    closeVideo(restoreFocus = true) {
      const player = this.videoStage?.querySelector('video');
      player?.pause();
      this.videoStage?.replaceChildren();
      if (this.dialog?.open) this.dialog.close();
      this.syncScrollLock();

      if (restoreFocus) this.activeTrigger?.focus();
      this.activeTrigger = null;
    }
  }

  customElements.define(elementName, TutorialVideoLibrary);
})();
